import React, { useEffect } from "react";
import { Ornament } from "./Ornament";
import Seo from "./Seo";
import { Phone, Mail, Instagram, MessageCircle } from "lucide-react";

// TODO: replace with real Calendly URL provided by Stamatina
const CALENDLY_URL =
  "https://calendly.com/stam-kanta/discovery-call?hide_gdpr_banner=1&background_color=fbf9f6&text_color=0a0a0a&primary_color=c9a050";

const useTop = () => {
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);
};

export default function BookCallPage() {
  useTop();

  // Load Calendly widget script once
  useEffect(() => {
    if (document.getElementById("calendly-script")) return;
    const s = document.createElement("script");
    s.id = "calendly-script";
    s.src = "https://assets.calendly.com/assets/external/widget.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  return (
    <div className="pt-32 bg-[var(--lu-ivory)]" data-testid="book-call-page">
      <Seo
        path="/book-a-call"
        title="Book a Discovery Call — 20-minute consultation with Stamatina Kanda | Lumière Entertainment"
        description="Book a free 20-minute discovery call directly with Stamatina Kanda, Founder of Lumière Entertainment, to discuss the concept of your event. Direct booking via Calendly."
        keywords="book discovery call, event planner Athens consultation, luxury event quote, Stamatina Kanda appointment, Calendly booking Lumière"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Book a Discovery Call", path: "/book-a-call" },
        ]}
      />

      <section className="container-lux text-center pb-4">
        <p className="kicker text-[var(--lu-gold-dark)]">
          20-Minute Consultation
        </p>
        <h1 className="mt-6 h-editorial text-[46px] sm:text-[64px] lg:text-[92px] text-[var(--lu-black)] leading-[0.98]">
          Book a <span className="italic text-[var(--lu-gold-dark)]">Discovery Call</span>
        </h1>
        <Ornament className="my-10" />
        <p className="text-[17px] font-serif italic text-[var(--lu-gold-dark)] max-w-2xl mx-auto">
          Every unforgettable night starts with a conversation.
        </p>
        <p className="mt-6 text-[15px] leading-[1.9] text-black-70 font-light max-w-2xl mx-auto">Book a free 20-minute call directly with Stamatina Kanda.
          We'll discuss your event's vision, space and style — and
          within 48 hours you will receive 2-3 curated concept directions in
          moodboard format.</p>
      </section>

      {/* Calendly inline embed */}
      <section className="section-y">
        <div className="container-lux">
          <div
            className="calendly-inline-widget mx-auto max-w-4xl border border-[var(--lu-gold-dark)]/25 shadow-sm"
            data-url={CALENDLY_URL}
            data-testid="calendly-widget"
            style={{ minWidth: "320px", height: "760px" }}
          />

          {/* Fallback CTAs */}
          <div className="mt-16 text-center">
            <p className="font-micro text-[10px] text-[var(--lu-gold-dark)]">Widget not working? Contact directly</p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
              <a
                href="https://wa.me/306943167877"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold-outline text-[11px] px-5 py-3 flex items-center gap-2"
                data-testid="book-call-whatsapp"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
              <a
                href="tel:+306943167877"
                className="btn-gold-outline text-[11px] px-5 py-3 flex items-center gap-2"
              >
                <Phone size={14} /> +30 694 316 7877
              </a>
              <a
                href="mailto:stamatina.kanta@yahoo.com"
                className="btn-gold-outline text-[11px] px-5 py-3 flex items-center gap-2"
              >
                <Mail size={14} /> stamatina.kanta@yahoo.com
              </a>
              <a
                href="https://instagram.com/stam_kanta"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold-outline text-[11px] px-5 py-3 flex items-center gap-2"
              >
                <Instagram size={14} /> @stam_kanta
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Reassurance */}
      <section className="section-y bg-[var(--lu-cream)]">
        <div className="container-lux max-w-3xl text-center">
          <p className="kicker text-[var(--lu-gold-dark)]">What to expect</p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            <div>
              <p className="font-serif text-[26px] text-[var(--lu-gold-dark)]">01</p>
              <p className="mt-3 font-serif text-[18px] text-[var(--lu-black)]">20-minute discovery call</p>
              <p className="mt-2 text-[13px] leading-[1.7] text-black-70 font-light">A quiet appointment. No dressed-up sales pitch —
                only creative discussion.</p>
            </div>
            <div>
              <p className="font-serif text-[26px] text-[var(--lu-gold-dark)]">02</p>
              <p className="mt-3 font-serif text-[18px] text-[var(--lu-black)]">
                2-3 concept directions
              </p>
              <p className="mt-2 text-[13px] leading-[1.7] text-black-70 font-light">Within 48 hours you receive curated moodboards with recommendations
                acts, palettes and soundtrack references.</p>
            </div>
            <div>
              <p className="font-serif text-[26px] text-[var(--lu-gold-dark)]">03</p>
              <p className="mt-3 font-serif text-[18px] text-[var(--lu-black)]">
                Bespoke proposal
              </p>
              <p className="mt-2 text-[13px] leading-[1.7] text-black-70 font-light">If we are the right fit, we proceed to full production
                proposal and we plan the evening together.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
